import { z } from 'zod';
import { VIEWPORT_CLASSES } from '../events/journey-event.schema.js';

/** Count of one answer key over the selection; `negative` comes from the prompt configuration. */
export const feedbackAnswerCountDto = z.object({
  answer: z.string(),
  /** The answer as the visitor read it, in the Console language; null when the prompt no longer defines it. */
  label: z.string().nullable(),
  count: z.number().int(),
  negative: z.boolean(),
});
export type FeedbackAnswerCount = z.infer<typeof feedbackAnswerCountDto>;

/**
 * Feedback aggregated on one subject (an entity, an announcement, a type): the
 * volume, the orientation and the demand score of functional §5.5.
 */
export const feedbackSubjectDto = z.object({
  /** Null for feedback whose page declared no issuing entity. */
  key: z.string().nullable(),
  responses: z.number().int(),
  negativeResponses: z.number().int(),
  negativeShare: z.number().min(0).max(1),
  demandScore: z.number().int().min(0).max(100),
  /** True under the project's minimal volume (functional §5.4 R1). */
  lowConfidence: z.boolean(),
  answers: z.array(feedbackAnswerCountDto),
});
export type FeedbackSubject = z.infer<typeof feedbackSubjectDto>;

/** One announcement, with the entity and the type it was declared under. */
export const feedbackAnnouncementDto = feedbackSubjectDto.extend({
  key: z.string(),
  entityKey: z.string().nullable(),
  announcementType: z.string().nullable(),
  /** Reference step the announcement page was promoted to, when known. */
  stepId: z.string().uuid().nullable(),
  stepName: z.string().nullable(),
  /** Path of the announcement page on the host site, when its step names it by a dynamic segment. */
  pagePath: z.string().nullable(),
});
export type FeedbackAnnouncement = z.infer<typeof feedbackAnnouncementDto>;

export const feedbackLanguageDto = z.object({
  language: z.string(),
  responses: z.number().int(),
  negativeShare: z.number().min(0).max(1),
});
export type FeedbackLanguage = z.infer<typeof feedbackLanguageDto>;

/** Values the collected feedback actually carries, so the filters offer real options. */
export const feedbackDimensionsDto = z.object({
  entityKeys: z.array(z.string()),
  announcementTypes: z.array(z.string()),
  languages: z.array(z.string()),
  viewports: z.array(z.enum(VIEWPORT_CLASSES)),
  configurationVersions: z.array(z.number().int()),
});
export type FeedbackDimensions = z.infer<typeof feedbackDimensionsDto>;

/** The "Avis recueillis" screen (functional §5.8): by entity, by announcement, by type, by language. */
export const feedbackQuestionDto = z.object({ promptId: z.string(), question: z.string() });
export type FeedbackQuestion = z.infer<typeof feedbackQuestionDto>;

/**
 * A comment a visitor added to an answer (ADR 0016), masked at reception. Read
 * with its announcement, never with its session: who wrote it is not known.
 */
export const feedbackCommentDto = z.object({
  occurredAt: z.string().datetime(),
  announcementKey: z.string(),
  announcementType: z.string().nullable(),
  entityKey: z.string().nullable(),
  promptId: z.string(),
  answer: z.string(),
  /** The answer as the visitor read it, in the Console language. */
  answerLabel: z.string().nullable(),
  negative: z.boolean(),
  title: z.string().nullable(),
  body: z.string().nullable(),
  language: z.string().nullable(),
});
export type FeedbackCommentView = z.infer<typeof feedbackCommentDto>;

/** At most this many comments are returned, the most recent first. */
export const FEEDBACK_COMMENTS_LIMIT = 50;

export const feedbackSummaryDto = z.object({
  period: z.object({ from: z.string().datetime(), to: z.string().datetime() }),
  /** The questions of the project as the visitor read them, in the Console language. */
  questions: z.array(feedbackQuestionDto),
  /** Questions displayed on the selected steps over the period. */
  promptsShown: z.number().int(),
  responses: z.number().int(),
  /** Responses ÷ questions shown, 0 when none was shown (functional §5.4 "taux de participation"). */
  participationRate: z.number().min(0).max(1),
  negativeShare: z.number().min(0).max(1),
  demandScore: z.number().int().min(0).max(100),
  lowConfidence: z.boolean(),
  byEntity: z.array(feedbackSubjectDto),
  byAnnouncement: z.array(feedbackAnnouncementDto),
  byAnnouncementType: z.array(feedbackSubjectDto),
  byLanguage: z.array(feedbackLanguageDto),
  dimensions: feedbackDimensionsDto,
  minimumSessionCount: z.number().int(),
  /** The latest comments under the same filters (ADR 0016). */
  comments: z.array(feedbackCommentDto),
});
export type FeedbackSummary = z.infer<typeof feedbackSummaryDto>;
